import dayjs, { Dayjs } from "dayjs"
import { EventFormData } from "./useEventFormState"

const repeatUnitsMap: { [key: number]: string } = {
  0: 'day',
  1: 'week',
  2: 'month',
  4: 'year',
}

export type RecurringFormValues = {
  repeatUnit: string
  repeatCount: number
  repeatPlace: string[]
  repeatQuarter: number[]
  repeatMonth: number[]
  repeatEndsOn: Dayjs | null
}

export const parseRepeatingEventConfig = (
  config: any,
  fallbackStartDate: Dayjs
): RecurringFormValues => {
  const unwrapped = config?.repeatingEventConfig || config

  if (!unwrapped) {
    return defaultRecurringFormValues(fallbackStartDate)
  }

  return {
    repeatUnit: typeof unwrapped.repeatUnits === 'number'
      ? repeatUnitsMap[unwrapped.repeatUnits] || 'day'
      : unwrapped.repeatUnits || 'day',
    repeatCount: unwrapped.repeatCount || 1,
    repeatPlace: unwrapped.repeatPlace?.split(',') || ["1st"],
    repeatQuarter: unwrapped.repeatQuarter?.split(',').map((q: string) => parseInt(q)) || [1],
    repeatMonth: unwrapped.repeatMonth?.split(',').map((m: string) => parseInt(m)) || [dayjs().month() + 1],
    repeatEndsOn: unwrapped.endDate ? dayjs(unwrapped.endDate) : null,
  }
}

export const defaultRecurringFormValues = (startDate: Dayjs): RecurringFormValues => ({
  repeatUnit: 'none',
  repeatCount: 1,
  repeatPlace: ["1st"],
  repeatQuarter: [1],
  repeatMonth: [startDate.month() + 1],
  repeatEndsOn: startDate,
})

export const buildRepeatingEventConfig = (formValues: EventFormData) => {
  const { startDate, repeatUnit, repeatCount, repeatPlace, repeatQuarter, repeatMonth, repeatEndsOn } = formValues

  if (repeatUnit === 'none') {
    return null
  }

  return {
    startDate: startDate.toISOString(),
    endDate: repeatEndsOn ? repeatEndsOn.toISOString() : null,
    repeatUnits: repeatUnit,
    repeatCount,
    repeatingDayUnit: startDate.format('dddd').toLowerCase(),
    repeatPlace: repeatPlace.length > 0 ? repeatPlace.join(',') : null,
    repeatQuarter: repeatQuarter.length > 0 ? repeatQuarter.join(',') : null,
    repeatMonth: repeatMonth.length > 0 ? repeatMonth.join(',') : null,
  }
}

export type TimingFields = {
  actualStartDateTime: Dayjs
  endDateTime: Dayjs
  durationMinutes: number
}

export const buildTimingFields = (formValues: EventFormData): TimingFields => {
  const { startDate, startTime, estimatedDuration, isAllDay, endDate, endDateMode } = formValues

  if (isAllDay) {
    const actualStartDateTime = startDate.startOf('day')
    const end = endDateMode === "sameDay" ? actualStartDateTime : endDate.startOf('day')
    return {
      actualStartDateTime,
      endDateTime: end,
      durationMinutes: end.diff(actualStartDateTime, 'minutes'),
    }
  }

  const actualStartDateTime = startDate
    .hour(startTime.hour())
    .minute(startTime.minute())
    .second(0)
  const durationMinutes = Math.floor(estimatedDuration.seconds / 60)

  return {
    actualStartDateTime,
    endDateTime: actualStartDateTime.add(durationMinutes, 'minutes'),
    durationMinutes,
  }
}
